import React, { useState, useEffect } from 'react';
import { Storage } from 'aws-amplify';
import FileViewer from './FileViewer';
import './ClientFileBrowser.css';

const ClientFileBrowser = ({ company }) => {
  const [folders, setFolders] = useState({});
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [error, setError] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);

  useEffect(() => {
    if (company) {
      loadFiles();
    }
  }, [company]);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);

    try {
      // List all files for this client
      // Files are stored in: clients/{company.id}/ path
      const clientIdentifier = company.id;
      const clientPath = `clients/${clientIdentifier}/`;
      console.log('Loading files from path:', clientPath);
      console.log('Company object:', company);
      console.log('Using client identifier:', clientIdentifier);

      // Start with public level (where both admin and client upload files)
      const result = await Storage.list(clientPath, {
        level: 'public',
        pageSize: 1000
      });

      // Get files from public level (where files are stored)
      const allFiles = result.results || result || [];
      console.log('All files to organize:', allFiles);
      const folderStructure = organizeFolderStructure(allFiles);
      console.log('Organized folder structure:', folderStructure);
      setFolders(folderStructure);

      // Auto-expand recent folders
      const recentFolders = getRecentFolders(folderStructure);
      setExpandedFolders(new Set(recentFolders));

    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load files. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const organizeFolderStructure = (files) => {
    const structure = {};

    files.forEach(file => {
      if (!file.key || file.key.endsWith('/')) return; // Skip folder markers

      const clientIdentifier = company.name?.toLowerCase() || company.id;
      const pathParts = file.key.replace(`clients/${clientIdentifier}/`, '').split('/');

      // Organize by date folders or category folders
      if (pathParts.length >= 2) {
        const folderName = pathParts[0];
        const fileName = pathParts[pathParts.length - 1];

        if (!structure[folderName]) {
          structure[folderName] = {
            name: folderName,
            date: parseDateFromFolder(folderName),
            files: [],
            totalSize: 0
          };
        }

        structure[folderName].files.push({
          name: fileName,
          key: file.key,
          size: file.size || 0,
          lastModified: file.lastModified,
          type: getFileType(fileName)
        });

        structure[folderName].totalSize += file.size || 0;
      }
    });

    return structure;
  };

  const parseDateFromFolder = (folderName) => {
    // Try to parse date from "Month YYYY" format (e.g., "September 2025")
    const monthYearMatch = folderName.match(/^(\w+)\s+(\d{4})$/);
    if (monthYearMatch) {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthName = monthYearMatch[1];
      const year = parseInt(monthYearMatch[2]);
      const monthIndex = monthNames.indexOf(monthName);

      if (monthIndex !== -1) {
        return new Date(year, monthIndex, 1);
      }
    }

    // Try to parse date from folder name (YYYY-MM-DD format) for backward compatibility
    const dateParts = folderName.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (dateParts) {
      return new Date(dateParts[1], dateParts[2] - 1, dateParts[3]);
    }

    // Fallback: try to create a date from the folder name
    const fallbackDate = new Date(folderName);
    return isNaN(fallbackDate.getTime()) ? new Date() : fallbackDate;
  };

  const getRecentFolders = (structure) => {
    // Get the 3 most recent folders
    return Object.keys(structure)
      .sort((a, b) => structure[b].date - structure[a].date)
      .slice(0, 3);
  };

  const getFileType = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const types = {
      pdf: 'pdf',
      doc: 'word', docx: 'word',
      xls: 'excel', xlsx: 'excel',
      ppt: 'powerpoint', pptx: 'powerpoint',
      jpg: 'image', jpeg: 'image', png: 'image', gif: 'image',
      mp4: 'video', avi: 'video', mov: 'video',
      zip: 'archive', rar: 'archive',
      html: 'web',
      txt: 'text', md: 'text'
    };
    return types[ext] || 'file';
  };

  const getFileIcon = (type) => {
    const icons = {
      pdf: '📄',
      word: '📝',
      excel: '📊',
      powerpoint: '📽️',
      image: '🖼️',
      video: '🎥',
      archive: '📦',
      web: '🌐',
      text: '📃',
      file: '📎'
    };
    return icons[type] || '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return d.toLocaleDateString('en-US', options);
  };

  const toggleFolder = (folderName) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderName)) {
      newExpanded.delete(folderName);
    } else {
      newExpanded.add(folderName);
    }
    setExpandedFolders(newExpanded);
  };

  const toggleFileSelection = (fileKey) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileKey)) {
      newSelected.delete(fileKey);
    } else {
      newSelected.add(fileKey);
    }
    setSelectedFiles(newSelected);
  };

  const selectAllInFolder = (folderName) => {
    const folder = folders[folderName];
    if (!folder) return;

    const newSelected = new Set(selectedFiles);
    const folderFileKeys = folder.files.map(f => f.key);
    const allSelected = folderFileKeys.every(key => selectedFiles.has(key));

    if (allSelected) {
      folderFileKeys.forEach(key => newSelected.delete(key));
    } else {
      folderFileKeys.forEach(key => newSelected.add(key));
    }

    setSelectedFiles(newSelected);
  };

  const downloadFile = async (file) => {
    try {
      let url;

      // Try public level first (where admin uploads new files)
      try {
        url = await Storage.get(file.key, {
          level: 'public',
          expires: 3600,
          download: false
        });
        console.log('Downloaded from public level:', file.key);
      } catch (publicErr) {
        console.log('Public level failed, trying protected level');
        // If public fails, try protected level
        try {
          url = await Storage.get(file.key, {
            level: 'protected',
            expires: 3600,
            download: false
          });
          console.log('Downloaded from protected level:', file.key);
        } catch (protectedErr) {
          console.log('Protected level failed, trying private level');
          // If protected fails, try private level (old uploads)
          url = await Storage.get(file.key, {
            level: 'private',
            expires: 3600,
            download: false
          });
          console.log('Downloaded from private level:', file.key);
        }
      }

      // Open in new tab
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading file:', err);
      setError(`Failed to download ${file.name}`);
    }
  };

  const downloadSelected = async () => {
    if (selectedFiles.size === 0) return;

    for (const fileKey of selectedFiles) {
      // Find the file object
      let fileToDownload = null;
      for (const folder of Object.values(folders)) {
        const file = folder.files.find(f => f.key === fileKey);
        if (file) {
          fileToDownload = file;
          break;
        }
      }

      if (fileToDownload) {
        await downloadFile(fileToDownload);
      }
    }

    setSelectedFiles(new Set());
  };

  const filterAndSortFolders = () => {
    let filteredFolders = { ...folders };

    // Filter by search term
    if (searchTerm) {
      filteredFolders = Object.fromEntries(
        Object.entries(folders).filter(([folderName, folder]) => {
          const matchesFolder = folderName.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesFiles = folder.files.some(file =>
            file.name.toLowerCase().includes(searchTerm.toLowerCase())
          );
          return matchesFolder || matchesFiles;
        })
      );
    }

    // Sort folders
    const sortedEntries = Object.entries(filteredFolders).sort((a, b) => {
      if (sortBy === 'date') {
        return b[1].date - a[1].date;
      } else if (sortBy === 'name') {
        return a[0].localeCompare(b[0]);
      } else if (sortBy === 'size') {
        return b[1].totalSize - a[1].totalSize;
      }
      return 0;
    });

    return Object.fromEntries(sortedEntries);
  };

  const sortedFolders = filterAndSortFolders();

  if (loading) {
    return (
      <div className="file-browser-loading">
        <div className="loading-spinner"></div>
        <p>Loading your files...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="file-browser-error">
        <p>{error}</p>
        <button onClick={loadFiles} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="client-file-browser">
      {/* Toolbar */}
      <div className="browser-toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="date">Sort by Date</option>
            <option value="name">Sort by Name</option>
            <option value="size">Sort by Size</option>
          </select>
        </div>

        <div className="toolbar-right">
          {selectedFiles.size > 0 && (
            <button onClick={downloadSelected} className="download-selected">
              Download Selected ({selectedFiles.size})
            </button>
          )}

          <button onClick={loadFiles} className="refresh-button" title="Refresh">
            🔄
          </button>
        </div>
      </div>

      {/* File Browser */}
      {Object.keys(sortedFolders).length === 0 ? (
        <div className="no-files">
          <p>No files available yet</p>
          <p className="no-files-hint">Files will appear here once uploaded by your administrator</p>
        </div>
      ) : (
        <div className="folders-container">
          {Object.entries(sortedFolders).map(([folderName, folder]) => (
            <div key={folderName} className="folder-section">
              <div
                className="folder-header"
                onClick={() => toggleFolder(folderName)}
              >
                <span className="folder-arrow">
                  {expandedFolders.has(folderName) ? '▼' : '▶'}
                </span>
                <span className="folder-icon">📁</span>
                <span className="folder-name">{folderName}</span>
                <span className="folder-stats">
                  {folder.files.length} files • {formatFileSize(folder.totalSize)}
                </span>
                {folder.files.length > 0 && (
                  <button
                    className="select-all-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectAllInFolder(folderName);
                    }}
                  >
                    {folder.files.every(f => selectedFiles.has(f.key)) ? '☑' : '☐'}
                  </button>
                )}
              </div>

              {expandedFolders.has(folderName) && (
                <div className="files-list">
                  {folder.files.map(file => (
                    <div
                      key={file.key}
                      className={`file-item ${selectedFiles.has(file.key) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.key)}
                        onChange={() => toggleFileSelection(file.key)}
                        className="file-checkbox"
                      />

                      <div className="file-icon">
                        {getFileIcon(file.type)}
                      </div>

                      <div className="file-info">
                        <div className="file-name">{file.name}</div>
                        <div className="file-meta">
                          {formatFileSize(file.size)} •
                          {file.lastModified && new Date(file.lastModified).toLocaleDateString()}
                        </div>
                      </div>

                      <div className="file-actions">
                        <button
                          onClick={() => setViewingFile(file)}
                          className="view-button"
                          title="View"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => downloadFile(file)}
                          className="download-button"
                          title="Download"
                        >
                          ⬇
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {viewingFile && (
        <FileViewer
          file={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </div>
  );
};

export default ClientFileBrowser;